import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from 'providers/AuthProvider';
import { getSessionId } from 'utils/authStorage';
import { trackError } from 'utils/trackError';
import { readWorkstationName } from 'utils/workstation';
import {
  type PatientOpened,
  type PatientSyncLocation,
  type PatientSyncState,
  usePatientSyncMachine,
} from './patientSyncMachine';

export type { PatientOpened, PatientSyncLocation, PatientSyncState };

interface PatientSyncContextType {
  patientOpened: PatientOpened | null;
  syncState: PatientSyncState;
  activePatient: PatientOpened | null;
  pendingPatient: PatientOpened | null;
  stopSync: () => void;
  startSync: () => void;
  reportManualSwitch: (patientId: string) => void;
  reportLocation: (location: PatientSyncLocation) => void;
  switchToPendingPatient: () => void;
  setConsultationPatientId: (patientId: string | null) => void;
  registerRecordingPause: (pause: () => Promise<void>) => () => void;
}

const PatientSyncContext = createContext<PatientSyncContextType | undefined>(undefined);
const ConsultationPatientContext = createContext<string | null>(null);

const UNAUTHORIZED_CLOSE_CODE = 4001;

const RECONNECT_BACKOFF_MS = [0, 1_000, 2_000, 5_000, 10_000, 30_000];
const STABLE_CONNECTION_MS = 5_000;

const apiBase = import.meta.env.VITE_API_BASE_URL as string;
const url = new URL(apiBase);
const PATIENT_SYNC_URL = `${url.protocol === 'https:' ? 'wss:' : 'ws:'}//${url.host}/patient-sync/`;

interface Identity {
  workstationName: string;
  sessionId: string;
}

export const PatientSyncProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const [patientOpened, setPatientOpened] = useState<PatientOpened | null>(null);
  const [consultationPatientId, setConsultationPatientId] = useState<string | null>(null);
  const pauseRecordingRef = useRef<(() => Promise<void>) | null>(null);
  const webSocketRef = useRef<WebSocket | null>(null);
  const identityRef = useRef<Identity | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stableTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generationRef = useRef(0);
  const connectRef = useRef<() => Promise<void>>(() => Promise.resolve());

  const sessionKey = isAuthenticated ? (user?.id ?? null) : null;
  const isConnected = sessionKey !== null;

  const {
    syncState,
    activePatient,
    pendingPatient,
    finishLoading,
    applyPendingPatient,
    stopSync,
    startSync,
    reportManualSwitch,
    reportLocation,
  } = usePatientSyncMachine(patientOpened, sessionKey, isConnected);
  const syncStateRef = useRef(syncState);
  syncStateRef.current = syncState;

  const disconnect = useCallback(() => {
    generationRef.current += 1;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (stableTimeoutRef.current) {
      clearTimeout(stableTimeoutRef.current);
      stableTimeoutRef.current = null;
    }
    const ws = webSocketRef.current;
    if (ws) {
      ws.onopen = null;
      ws.onclose = null;
      ws.onmessage = null;
      ws.close();
      webSocketRef.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    const attempt = reconnectAttemptRef.current;
    reconnectAttemptRef.current += 1;
    const delay = RECONNECT_BACKOFF_MS[Math.min(attempt, RECONNECT_BACKOFF_MS.length - 1)];
    reconnectTimeoutRef.current = setTimeout(() => {
      void connectRef.current();
    }, delay);
  }, []);

  const resolveIdentity = useCallback(async (): Promise<Identity | null> => {
    if (identityRef.current) return identityRef.current;
    const generation = generationRef.current;
    const workstationName = await readWorkstationName();
    if (!workstationName) return null;
    const sessionId = await getSessionId();
    if (!sessionId) return null;
    if (generation !== generationRef.current) return null;
    const identity = { workstationName, sessionId };
    identityRef.current = identity;
    return identity;
  }, []);

  const connect = useCallback(async () => {
    disconnect();
    const generation = generationRef.current;

    let identity: Identity | null;
    try {
      identity = await resolveIdentity();
    } catch {
      if (generation !== generationRef.current) return;
      finishLoading();
      scheduleReconnect();
      return;
    }
    if (generation !== generationRef.current) return;
    if (!identity) {
      finishLoading();
      return;
    }

    const webSocket = new WebSocket(PATIENT_SYNC_URL, ['session', identity.sessionId]);
    webSocketRef.current = webSocket;

    webSocket.onopen = () => {
      stableTimeoutRef.current = setTimeout(() => {
        reconnectAttemptRef.current = 0;
      }, STABLE_CONNECTION_MS);
      try {
        webSocket.send(
          JSON.stringify({ type: 'config', workstationName: identity.workstationName })
        );
      } catch {
        return;
      }
    };

    webSocket.onmessage = (event: MessageEvent) => {
      if (generation !== generationRef.current) return;
      let message: Record<string, unknown>;
      try {
        message = JSON.parse(event.data as string) as Record<string, unknown>;
      } catch {
        return;
      }
      if (message.type === 'patient-opened' && typeof message.patientId === 'string') {
        setPatientOpened({
          patientId: message.patientId,
          fullName: typeof message.fullName === 'string' ? message.fullName : null,
        });
      }
      if (message.type === 'no-patient-opened') {
        if (syncStateRef.current === 'loading') toast.info(t('patientSync.noRecentPatient'));
        finishLoading();
      }
    };

    webSocket.onclose = (event: CloseEvent) => {
      if (stableTimeoutRef.current) {
        clearTimeout(stableTimeoutRef.current);
        stableTimeoutRef.current = null;
      }
      webSocketRef.current = null;
      finishLoading();
      if (event.code === UNAUTHORIZED_CLOSE_CODE) return;
      scheduleReconnect();
    };
  }, [disconnect, finishLoading, resolveIdentity, scheduleReconnect, t]);

  connectRef.current = connect;

  useEffect(() => {
    identityRef.current = null;
    setPatientOpened(null);
    if (!isConnected) {
      disconnect();
      return;
    }
    void connect();
    return disconnect;
  }, [isConnected, sessionKey, connect, disconnect]);

  const registerRecordingPause = useCallback((pause: () => Promise<void>) => {
    pauseRecordingRef.current = pause;
    return () => {
      if (pauseRecordingRef.current === pause) pauseRecordingRef.current = null;
    };
  }, []);

  const switchToPendingPatient = useCallback(() => {
    const pause = pauseRecordingRef.current;
    if (pause)
      void pause()
        .catch((error: unknown) => trackError(error, { context: 'patientSwitchPause' }))
        .then(applyPendingPatient);
    else applyPendingPatient();
  }, [applyPendingPatient]);

  const value = useMemo<PatientSyncContextType>(
    () => ({
      patientOpened,
      syncState,
      activePatient,
      pendingPatient,
      stopSync,
      startSync,
      reportManualSwitch,
      reportLocation,
      switchToPendingPatient,
      setConsultationPatientId,
      registerRecordingPause,
    }),
    [
      patientOpened,
      syncState,
      activePatient,
      pendingPatient,
      stopSync,
      startSync,
      reportManualSwitch,
      reportLocation,
      switchToPendingPatient,
      registerRecordingPause,
    ]
  );

  return (
    <PatientSyncContext.Provider value={value}>
      <ConsultationPatientContext.Provider value={consultationPatientId}>
        {children}
      </ConsultationPatientContext.Provider>
    </PatientSyncContext.Provider>
  );
};

export const usePatientSync = (): PatientSyncContextType => {
  const ctx = useContext(PatientSyncContext);
  if (!ctx) {
    throw new Error('usePatientSync must be used within a PatientSyncProvider');
  }
  return ctx;
};

export const useConsultationPatientId = (): string | null => useContext(ConsultationPatientContext);
